import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { DEFAULT_ENABLE_LOGO_NAVIGATION, DEFAULT_SHOW_LOGO_ON_FOOTER, FONTS_WITH_TINYMCE_GOOGLE_FONTS } from '@opengov/stories-shared/constants';
import { IPublicationBrandings } from '@opengov/stories-shared/types';
import React, { useMemo } from 'react';

import { IOnlinePublicationMessages } from '../../../../../types';
import { ContrastColorsSettings } from '../../../../common/components/ContrastColorsSettings';
import { IPreviewMenuItem, NavBarPreview } from '../NavBarPreview';
import { PublicationLogoUpload } from '../PublicationLogoUpload';

import { styles } from './PublicationBrandingTab.styles';

export type IPublicationBrandingsProps = {
  entitySubdomain: string;
  publicationBrandings: IPublicationBrandings | null;
  messages: IOnlinePublicationMessages;
  handleUpdateBrandings(payload: Partial<IPublicationBrandings>): void;
};

export const PublicationBrandingTab = (props: IPublicationBrandingsProps) => {
  const { entitySubdomain, messages, publicationBrandings, handleUpdateBrandings } = props;
  const fonts = FONTS_WITH_TINYMCE_GOOGLE_FONTS;
  const fontSelectLabelId = 'font-style-select-label';

  const previewMenuItems = useMemo<IPreviewMenuItem[]>(
    () => [
      { displayName: `${messages.sampleMenu} 1`, children: [{ displayName: `${messages.sampleMenu} child` }] },
      { displayName: `${messages.sampleMenu} 2` }
    ],
    [messages]
  );

  const renderLogoSettings = () => (
    <>
      <PublicationLogoUpload
        entitySubdomain={entitySubdomain}
        messages={messages}
        logoUrl={publicationBrandings?.logoUrl ?? null}
        onUrlChange={(url) => handleUpdateBrandings({ logoUrl: url })}
      />
      <FormControlLabel
        sx={styles.checkboxOption}
        data-test="show-logo-on-footer-checkbox"
        label={messages.showLogoOnFooter}
        control={
          <Checkbox
            size="small"
            checked={publicationBrandings?.showLogoOnFooter ?? DEFAULT_SHOW_LOGO_ON_FOOTER}
            onChange={(e) => handleUpdateBrandings({ showLogoOnFooter: !!e.target.checked })}
          />
        }
      />
      <FormControlLabel
        sx={styles.checkboxOption}
        data-test="enable-logo-navigation-checkbox"
        label={messages.enableLogoNavigation}
        control={
          <Checkbox
            size="small"
            checked={publicationBrandings?.enableLogoNavigation ?? DEFAULT_ENABLE_LOGO_NAVIGATION}
            onChange={(e) => handleUpdateBrandings({ enableLogoNavigation: !!e.target.checked })}
          />
        }
      />
    </>
  );

  const renderFontSettings = () => (
    <>
      <Box sx={styles.fontSettingsBlock}>
        <Typography variant="subtitle1">{messages.style}</Typography>
        <Typography variant="body2" color="text.secondary">
          {messages.styleNavBarContent}
        </Typography>
      </Box>
      <FormControl sx={styles.fontStyleSelect}>
        <InputLabel id={fontSelectLabelId}>{messages.fontStyle}</InputLabel>
        <Select
          labelId={fontSelectLabelId}
          inputProps={{ 'aria-label': messages.fontStyle }}
          label={messages.fontStyle}
          data-test="font-style-select"
          value={publicationBrandings?.fontStyle ?? ''}
          onChange={(e: SelectChangeEvent<string>) => handleUpdateBrandings({ fontStyle: e.target.value })}
        >
          {fonts.map((font) => (
            <MenuItem data-test="font-style-select-option" key={font.id} value={font.id}>
              {font.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={styles.fontSettingsBlock}>
        <Typography variant="body2" sx={styles.sectionLabel}>
          {messages.idleMenuItems.toUpperCase()}
        </Typography>
        <ContrastColorsSettings
          dataTest="idle-menu-items-colors-settings"
          primaryColorTitle={messages.navBarColor}
          primaryColors={publicationBrandings?.idleBackgroundColor || []}
          secondaryColorTitle={messages.fontColor}
          secondaryColors={publicationBrandings?.idleFontColor || []}
          onChangePrimaryColor={(colorOptions) => handleUpdateBrandings({ idleBackgroundColor: colorOptions })}
          onChangeSecondaryColor={(colorOptions) => handleUpdateBrandings({ idleFontColor: colorOptions })}
        />
      </Box>
      <Box sx={styles.fontSettingsBlock}>
        <Typography variant="body2" sx={styles.sectionLabel}>
          {messages.activeMenuItems.toUpperCase()}
        </Typography>
        <ContrastColorsSettings
          dataTest="active-menu-items-colors-settings"
          primaryColorTitle={messages.highlightColor}
          primaryColors={publicationBrandings?.activeBackgroundColor || []}
          secondaryColorTitle={messages.fontColor}
          secondaryColors={publicationBrandings?.activeFontColor || []}
          onChangePrimaryColor={(colorOptions) => handleUpdateBrandings({ activeBackgroundColor: colorOptions })}
          onChangeSecondaryColor={(colorOptions) => handleUpdateBrandings({ activeFontColor: colorOptions })}
        />
      </Box>
    </>
  );

  return (
    <Box sx={styles.container} data-test="publication-branding-tab">
      <Box sx={styles.layoutColumn}>
        {renderLogoSettings()}
        {renderFontSettings()}
      </Box>
      <Box sx={styles.layoutColumn}>
        <Typography variant="body2" sx={styles.sectionLabel}>
          {messages.preview.toUpperCase()}
        </Typography>
        <NavBarPreview publicationBrandings={publicationBrandings} menuItems={previewMenuItems} />
      </Box>
    </Box>
  );
};
